/**
 * Shared helpers for displaying and exporting scope + Additional Scope tags (US 13961).
 */

export const ADDITIONAL_SCOPE_CHIP_CLASS =
  "inline-flex items-center rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300";

export const ADDITIONAL_SCOPE_DIVIDER_CLASS = "h-5 w-px bg-gray-300 mx-1";

export function formatCapabilitiesLabels(capabilities) {
  if (!capabilities?.length) return "";
  return capabilities
    .map(
      (c) =>
        c?.rfi_name ||
        c?.name ||
        c?.service_name ||
        c?.services ||
        c?.geographical_region ||
        ""
    )
    .filter(Boolean)
    .join(", ");
}

/**
 * Export formatting: predefined services first, then ` | `, then additional scope tags.
 */
export function formatScopeWithAdditionalScopeForExport(
  capabilities,
  additionalScope
) {
  const services = formatCapabilitiesLabels(capabilities);
  const tags = Array.isArray(additionalScope)
    ? additionalScope.filter(Boolean).join(", ")
    : "";
  if (!tags) return services;
  return services ? `${services} | ${tags}` : tags;
}

/**
 * Library / My Documents: show additional scope on audit rows and custom-scope RFIs only.
 */
export function shouldShowAdditionalScopeOnLibraryItem(item) {
  if (!item?.additional_scope?.length) return false;
  const docType = String(
    item?.document_type || item?.entity_type || ""
  ).toLowerCase();
  if (docType === "vqa") return true;
  if (docType === "rfi") {
    if (item?.is_global_template === true) return false;
    const name = String(item?.document_name || "").toLowerCase();
    if (name.startsWith("diligent")) return false;
    return true;
  }
  return false;
}

/**
 * Per-sponsor scope add-on custom tags are stored as a delimited string on the
 * DTO, unlike `additional_scope` which the backend already deserializes into an array.
 * Parses JSON-array text first (matches the backend's own storage format), falls back to
 * comma/semicolon splitting, and never splits a plain string into individual characters.
 */
export function parseDelimitedScopeTags(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
  } catch {
    // Not JSON — fall through to delimiter splitting.
  }
  return String(raw)
    .split(/[,;]/)
    .map((tag) => tag.trim())
    .filter(Boolean);
}

/**
 * CSV export text for a join sub-request row's Added scope delta: baseline
 * scope is formatted separately by `formatScopeWithAdditionalScopeForExport`; this
 * covers only the pending/confirmed add-on services and custom tags.
 */
export function formatAddedScopeForExport(addedScope, addedScopeTags) {
  const serviceNames = Array.isArray(addedScope)
    ? addedScope.map((item) => item?.rfi_name).filter(Boolean)
    : [];
  const tags = parseDelimitedScopeTags(addedScopeTags);
  const all = [...serviceNames, ...tags];
  if (!all.length) return "";
  return `Added: ${all.join(", ")}`;
}

export function additionalScopeMatchesSearch(additionalScope, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q || !Array.isArray(additionalScope) || additionalScope.length === 0) {
    return false;
  }
  return additionalScope.some((tag) =>
    String(tag || "")
      .toLowerCase()
      .includes(q)
  );
}
